import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { medicamentoService } from '../services';
import { 
  ArrowLeft, 
  Package, 
  Edit,
  Trash2,
  CheckCircle,
  BarChart3,
  Clock,
  ShieldAlert,
  Pill,
  Hash,
  Archive,
  AlertCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const DetalhesGrupoMedicamento = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [medicamentos, setMedicamentos] = useState([]);
  const [grupo, setGrupo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const carregarDetalhesGrupo = async () => {
      try {
        setLoading(true);
        
        // Tenta obter o ID do medicamento registrado da URL
        const medicamentoRegistradoId = searchParams.get('id');
        
        if (medicamentoRegistradoId) {
          // Nova API - usando ID do medicamento registrado
          const response = await medicamentoService.getDetalhesGrupoPorId(medicamentoRegistradoId);
          setMedicamentos(response.itensEstoque || []);
          setGrupo(response.medicamento || {});
        } else {
          // Fallback para API antiga
          const grupoParams = {
            nome: searchParams.get('nome'),
            principioAtivo: searchParams.get('principioAtivo'),
            fabricante: searchParams.get('fabricante'),
            codigoBarras: searchParams.get('codigoBarras'),
            dosagem: searchParams.get('dosagem'),
            formaFarmaceutica: searchParams.get('formaFarmaceutica'),
            categoria: searchParams.get('categoria'),
            tipoControle: searchParams.get('tipoControle'),
            prescricaoObrigatoria: searchParams.get('prescricaoObrigatoria')
          };

          const response = await medicamentoService.getDetalhesGrupo(grupoParams);
          setMedicamentos(response.medicamentos || []);
          setGrupo(response.grupo || {});
        }
      } catch (error) {
        console.error('Erro ao carregar detalhes do grupo:', error);
        setMessage({ type: 'error', text: 'Erro ao carregar detalhes do grupo' });
      } finally {
        setLoading(false);
      }
    };
    
    carregarDetalhesGrupo();
  }, [searchParams]);

  const handleDelete = async (estoqueId, lote) => {
    if (user.cargo !== 'admin') {
      setMessage({ type: 'error', text: 'Apenas administradores podem excluir itens do estoque' });
      return;
    }

    if (window.confirm(`Tem certeza que deseja excluir o item do lote "${lote}"?`)) {
      try {
        await medicamentoService.delete(estoqueId);
        setMessage({ type: 'success', text: 'Item do estoque excluído com sucesso' });
        // Recarregar dados
        window.location.reload();
      } catch (error) {
        console.error('Erro ao excluir item do estoque:', error);
        setMessage({ type: 'error', text: 'Erro ao excluir item do estoque' });
      }
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarTipoControle = (tipo) => {
    const tipos = {
      'sem_controle': 'Sem Controle',
      'normal': 'Controle Normal',
      'especial': 'Controle Especial'
    };
    return tipos[tipo] || tipo;
  };

  const getCorTipoControle = (tipo) => {
    const cores = {
      'sem_controle': '#28a745',
      'normal': '#ffc107',
      'especial': '#dc3545'
    };
    return cores[tipo] || '#6c757d';
  };

  const getStatusValidade = (dataValidade) => {
    const hoje = new Date();
    const validade = new Date(dataValidade);
    const diffTime = validade.getTime() - hoje.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'vencido', cor: '#dc3545', texto: 'Vencido' };
    } else if (diffDays <= 30) {
      return { status: 'vencendo', cor: '#ffc107', texto: `Vence em ${diffDays} dias` };
    } else {
      return { status: 'normal', cor: '#28a745', texto: 'Válido' };
    }
  };

  const getStatusEstoque = (estoque, estoqueMinimo) => {
    if (estoque === 0) {
      return { status: 'zerado', cor: '#dc3545', icone: XCircle };
    } else if (estoque <= estoqueMinimo) {
      return { status: 'baixo', cor: '#ffc107', icone: AlertTriangle };
    } else {
      return { status: 'normal', cor: '#28a745', icone: CheckCircle };
    }
  };

  if (loading) {
    return (
      <div className="medicamentos-container">
        <div className="loading">Carregando detalhes do grupo...</div>
      </div>
    );
  }

  if (!grupo) {
    return (
      <div className="medicamentos-container">
        <div className="error">Grupo não encontrado</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/medicamentos')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            marginBottom: '20px'
          }}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: 'bold', 
          color: '#333',
          margin: '0 0 8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Package size={32} color="#007bff" />
          Detalhes do Grupo: {grupo.nome}
        </h1>
        
        <p style={{ color: '#667', margin: '0', fontSize: '16px' }}>
          Gerenciando todos os lotes e estoque deste medicamento
        </p>
      </div>

      {message.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          backgroundColor: message.type === 'error' ? '#f8d7da' : '#d1edff',
          color: message.type === 'error' ? '#721c24' : '#0c5460',
          border: `1px solid ${message.type === 'error' ? '#f5c6cb' : '#bee5eb'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Cards de Informações */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        {/* Card Principal */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Pill size={20} color="#007bff" />
            Informações Principais
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <strong style={{ color: '#495057' }}>Princípio Ativo:</strong>
              <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                {grupo.principioAtivo || 'Não informado'}
              </span>
            </div>
            
            <div>
              <strong style={{ color: '#495057' }}>Código de Barras:</strong>
              <span style={{ 
                marginLeft: '8px', 
                color: '#6c757d',
                fontFamily: 'monospace',
                backgroundColor: '#f8f9fa',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Hash size={14} />
                {grupo.codigoBarras || 'Não informado'}
              </span>
            </div>

            {grupo.dosagem && (
              <div>
                <strong style={{ color: '#495057' }}>Dosagem:</strong>
                <span style={{ marginLeft: '8px', color: '#6c757d' }}>{grupo.dosagem}</span>
              </div>
            )}

            {grupo.formaFarmaceutica && (
              <div>
                <strong style={{ color: '#495057' }}>Forma Farmacêutica:</strong>
                <span style={{ marginLeft: '8px', color: '#6c757d' }}>{grupo.formaFarmaceutica}</span>
              </div>
            )}
          </div>
        </div>

        {/* Card Controle e Categoria */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ShieldAlert size={20} color="#dc3545" />
            Controle e Categoria
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <strong style={{ color: '#495057', marginBottom: '8px', display: 'block' }}>Tipo de Controle:</strong>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: getCorTipoControle(grupo.tipoControle || 'sem_controle') + '20',
                color: getCorTipoControle(grupo.tipoControle || 'sem_controle'),
                border: `1px solid ${getCorTipoControle(grupo.tipoControle || 'sem_controle')}`
              }}>
                {formatarTipoControle(grupo.tipoControle || 'sem_controle')}
              </span>
            </div>

            {grupo.categoria && (
              <div>
                <strong style={{ color: '#495057', marginBottom: '8px', display: 'block' }}>Categoria:</strong>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  border: '1px solid #bbdefb'
                }}>
                  {grupo.categoria}
                </span>
              </div>
            )}

            <div>
              <strong style={{ color: '#495057', marginBottom: '8px', display: 'block' }}>Prescrição:</strong>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: grupo.prescricaoObrigatoria === 'true' ? '#fff3cd' : '#d4edda',
                color: grupo.prescricaoObrigatoria === 'true' ? '#856404' : '#155724',
                border: grupo.prescricaoObrigatoria === 'true' ? '1px solid #ffeaa7' : '1px solid #c3e6cb'
              }}>
                {grupo.prescricaoObrigatoria === 'true' ? (
                  <>
                    <AlertCircle size={14} />
                    Obrigatória
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} />
                    Não Obrigatória
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Card Estatísticas */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          gridColumn: 'span 2'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <BarChart3 size={20} color="#28a745" />
            Resumo do Estoque
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff', marginBottom: '4px' }}>
                {medicamentos.length}
              </div>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Total de Lotes</div>
            </div>
            
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745', marginBottom: '4px' }}>
                {medicamentos.reduce((total, med) => total + parseInt(med.estoque), 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Estoque Total</div>
            </div>
            
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545', marginBottom: '4px' }}>
                {medicamentos.filter(med => getStatusValidade(med.dataValidade).status === 'vencido').length}
              </div>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Lotes Vencidos</div>
            </div>
            
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107', marginBottom: '4px' }}>
                {medicamentos.filter(med => getStatusValidade(med.dataValidade).status === 'vencendo').length}
              </div>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Vencendo em 30 dias</div>
            </div>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Lista de Medicamentos por Lote */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e9ecef'
        }}>
          <h3 style={{ 
            margin: '0', 
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Archive size={20} color="#6c757d" />
            Lotes Disponíveis
            <span style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {medicamentos.length}
            </span>
          </h3>
        </div>

        {medicamentos.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6c757d'
          }}>
            <Package size={64} color="#dee2e6" />
            <h3 style={{ margin: '16px 0 8px 0', color: '#495057' }}>Nenhum lote encontrado</h3>
            <p style={{ margin: '0', fontSize: '14px' }}>Este grupo não possui medicamentos cadastrados.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {medicamentos.map((medicamento) => {
              const statusEstoque = getStatusEstoque(medicamento.estoque, medicamento.estoqueMinimo);
              const statusValidade = getStatusValidade(medicamento.dataValidade);
              const StatusIcon = statusEstoque.icone;

              return (
                <div
                  key={medicamento.id}
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#fafbfc',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '16px',
                    alignItems: 'center'
                  }}>
                    {/* Lote Info */}
                    <div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>
                        LOTE
                      </div>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#333',
                        fontFamily: 'monospace',
                        backgroundColor: 'white',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6',
                        display: 'inline-block'
                      }}>
                        {medicamento.lote || 'Sem lote'}
                      </div>
                    </div>

                    {/* Estoque */}
                    <div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>
                        ESTOQUE
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <StatusIcon 
                          size={20} 
                          color={statusEstoque.cor}
                        />
                        <span style={{ 
                          fontSize: '18px', 
                          fontWeight: 'bold', 
                          color: statusEstoque.cor 
                        }}>
                          {medicamento.estoque}
                        </span>
                        <span style={{ fontSize: '14px', color: '#6c757d' }}>
                          / mín. {medicamento.estoqueMinimo}
                        </span>
                      </div>
                    </div>

                    {/* Validade */}
                    <div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>
                        VALIDADE
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '14px', color: '#333' }}>
                          {formatarData(medicamento.dataValidade)}
                        </div>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 6px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          backgroundColor: statusValidade.cor + '20',
                          color: statusValidade.cor,
                          width: 'fit-content'
                        }}>
                          {statusValidade.status === 'vencido' && <AlertCircle size={12} />}
                          {statusValidade.status === 'vencendo' && <Clock size={12} />}
                          {statusValidade.status === 'valido' && <CheckCircle size={12} />}
                          {statusValidade.texto}
                        </span>
                      </div>
                    </div>

                    {/* Ações */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontWeight: '500' }}>
                        AÇÕES
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => navigate(`/medicamentos/editar/${medicamento.id}`)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          <Edit size={14} />
                          Editar
                        </button>
                        
                        {user.cargo === 'admin' && (
                          <button
                            onClick={() => handleDelete(medicamento.id, medicamento.lote)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            <Trash2 size={14} />
                            Excluir
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalhesGrupoMedicamento;